import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY_RULES = { name: "", min_ltv: "", min_orders: "", dormant_days: "", metal: "", tag: "", min_affinity: "" };

export default function CrmCustomers() {
  const [params, setParams] = useSearchParams();
  const segment = params.get("segment") || "";
  const q = params.get("q") || "";
  const [data, setData] = useState(null);
  const [custom, setCustom] = useState([]);
  const [builder, setBuilder] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_RULES });

  const loadCustom = () => api.get("/admin/crm/segments").then((r) => setCustom(r.data)).catch(() => {});
  useEffect(() => { loadCustom(); }, []);

  const saveSegment = async () => {
    const rules = {};
    for (const k of ["min_ltv", "min_orders", "dormant_days", "min_affinity"]) {
      if (draft[k] !== "" && Number(draft[k]) > 0) rules[k] = Number(draft[k]);
    }
    if (draft.metal) rules.metal = draft.metal;
    if (draft.tag.trim()) rules.tag = draft.tag.trim();
    try {
      const r = await api.post("/admin/crm/segments", { name: draft.name, rules });
      toast.success("Segment saved — counts recompute live");
      setBuilder(false);
      setDraft({ ...EMPTY_RULES });
      loadCustom();
      setParam("segment", `custom:${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteSegment = async (id) => {
    await api.delete(`/admin/crm/segments/${id}`).catch((e) => toast.error(errMsg(e)));
    if (segment === `custom:${id}`) setParam("segment", "");
    loadCustom();
  };

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/crm/customers", { params: { segment, q } }).then((r) => setData(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [segment, q]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4" data-testid="crm-customers-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Customers 360</h1>
      <div className="flex flex-wrap gap-2">
        <Input value={q} onChange={(e) => setParam("q", e.target.value)} placeholder="Search name, email, phone…"
          className="w-64 bg-white" data-testid="crm-customer-search" />
        <select value={segment} onChange={(e) => setParam("segment", e.target.value)}
          className="h-9 border border-neutral-300 bg-white px-3 text-sm" data-testid="crm-segment-filter">
          <option value="">All segments</option>
          <optgroup label="Prebuilt">
            {Object.entries(data?.segment_labels || {}).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </optgroup>
          {!!custom.length && (
            <optgroup label="Custom">
              {custom.map((s) => <option key={s.id} value={`custom:${s.id}`}>{s.name} ({s.count})</option>)}
            </optgroup>
          )}
        </select>
        <Button variant="outline" size="sm" onClick={() => setBuilder(!builder)} data-testid="segment-builder-toggle">
          {builder ? "Close builder" : "Build segment"}
        </Button>
      </div>

      {builder && (
        <div className="bg-white border border-neutral-200 p-4 space-y-3" data-testid="segment-builder">
          <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Custom segment — rules AND together; counts recompute live on every load</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Segment name" className="h-8 text-xs bg-white" data-testid="seg-name" />
            <Input type="number" value={draft.min_ltv} onChange={(e) => setDraft({ ...draft, min_ltv: e.target.value })} placeholder="Min LTV ₹" className="h-8 text-xs bg-white" data-testid="seg-min-ltv" />
            <Input type="number" value={draft.min_orders} onChange={(e) => setDraft({ ...draft, min_orders: e.target.value })} placeholder="Min orders" className="h-8 text-xs bg-white" data-testid="seg-min-orders" />
            <Input type="number" value={draft.dormant_days} onChange={(e) => setDraft({ ...draft, dormant_days: e.target.value })} placeholder="Dormant ≥ days" className="h-8 text-xs bg-white" data-testid="seg-dormant" />
            <select value={draft.metal} onChange={(e) => setDraft({ ...draft, metal: e.target.value })} className="h-8 border border-neutral-300 bg-white px-2 text-xs" data-testid="seg-metal">
              <option value="">Any metal</option><option value="gold">Gold-leaning</option><option value="silver">Silver-leaning</option>
            </select>
            <Input value={draft.tag} onChange={(e) => setDraft({ ...draft, tag: e.target.value })} placeholder="Profile tag" className="h-8 text-xs bg-white" data-testid="seg-tag" />
            <Input type="number" value={draft.min_affinity} onChange={(e) => setDraft({ ...draft, min_affinity: e.target.value })} placeholder="Min affinity signals" className="h-8 text-xs bg-white" data-testid="seg-min-affinity" />
            <Button size="sm" className="bg-neutral-900 text-white h-8" onClick={saveSegment} data-testid="seg-save">Save segment</Button>
          </div>
        </div>
      )}
      {!!custom.length && (
        <div className="flex flex-wrap gap-2" data-testid="custom-segment-list">
          {custom.map((s) => (
            <span key={s.id} className="text-xs border border-neutral-200 bg-white px-2 py-1 flex items-center gap-2">
              {s.name} <span className="font-mono text-brand" data-testid={`segment-count-${s.id}`}>{s.count}</span>
              <button onClick={() => deleteSegment(s.id)} className="text-red-500" data-testid={`segment-delete-${s.id}`}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="p-3">Customer</th><th className="p-3">Phone</th><th className="p-3">Orders</th>
              <th className="p-3 text-right">LTV</th><th className="p-3">Segments</th><th className="p-3">Last order</th>
            </tr>
          </thead>
          <tbody>
            {(data?.customers || []).map((c) => (
              <tr key={c.email} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`crm-customer-row-${c.email.replace(/[^a-z0-9]/gi, "-")}`}>
                <td className="p-3">
                  <Link to={`/admin/crm/customers/${encodeURIComponent(c.email)}`} className="font-medium text-brand hover:underline" data-testid={`crm-open-${c.email.replace(/[^a-z0-9]/gi, "-")}`}>
                    {c.name || "—"}
                  </Link>
                  <p className="text-xs text-neutral-400">{c.email}</p>
                </td>
                <td className="p-3 font-mono text-xs">{c.phone || "—"}</td>
                <td className="p-3 font-mono">{c.order_count}</td>
                <td className="p-3 text-right font-medium">{inr(c.ltv)}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {c.segments.map((s) => (
                      <span key={s} className="text-[10px] font-mono uppercase tracking-wider bg-neutral-100 px-1.5 py-0.5">{s}</span>
                    ))}
                  </div>
                </td>
                <td className="p-3 text-neutral-500">{c.last_order ? fmtDate(c.last_order) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.customers.length && <p className="p-6 text-center text-sm text-neutral-500" data-testid="crm-no-customers">No customers match.</p>}
      </div>
    </div>
  );
}
